/**
 * Alt Attribute Transformer
 * Transforms WooRank images criteria data into React component-ready format
 */

import {
  getStatus,
  getIconType,
  getImportance,
  getSolvability,
} from '../../constants/seoMappings';

/**
 * Calculates image statistics from the images data
 * @param {Array} data - The images data array
 * @returns {Object} - Statistics { totalImages, withAlt, missingAlt, missingAltImages }
 */
const calculateImageStats = (data = []) => {
  if (!data || data?.length === 0) {
    return {
      totalImages: 0,
      withAlt: 0,
      missingAlt: 0,
      missingAltImages: [],
    };
  }

  const missingAltImages = (data || []).filter((img) => {
    // Check hasAlt property if available, otherwise check alt string
    if (typeof img?.hasAlt === 'boolean') {
      return !img?.hasAlt;
    }
    return !img?.alt || img?.alt?.trim() === '';
  });

  return {
    totalImages: data?.length || 0,
    withAlt: (data?.length || 0) - (missingAltImages?.length || 0),
    missingAlt: missingAltImages?.length || 0,
    missingAltImages: (missingAltImages || []).map((img) => ({
      src: img?.src || '',
      alt: img?.alt || '',
    })),
  };
};

/**
 * Generates a scenario-based message for alt attributes
 * @param {Object} stats - The image statistics
 * @returns {string} - The appropriate message based on the scenario
 */
const generateMessage = (stats = {}) => {
  const { totalImages = 0, withAlt = 0, missingAlt = 0 } = stats;

  // No images found
  if (totalImages === 0) {
    return 'No images were found on this page.';
  }

  // All images have alt text
  if (missingAlt === 0) {
    return `Great! All ${totalImages} image${totalImages === 1 ? '' : 's'} have alt attributes.`;
  }

  // Some images missing alt
  return `We found ${totalImages} image${totalImages === 1 ? '' : 's'} on this page. ${missingAlt} ${missingAlt === 1 ? 'is' : 'are'} missing alt attributes.`;
};

/**
 * Transforms the images criteria from WooRank API response
 * @param {Object} imagesCriteria - The images criteria object from the API
 * @returns {Object} - Transformed alt attribute data for React components
 */
export const transformAltAttribute = (imagesCriteria) => {
  // Handle missing criteria entirely
  if (!imagesCriteria) {
    return {
      status: 'error',
      iconType: 'error',
      importance: { level: 0, text: '' },
      solvability: { level: 0, text: '' },
      isEmpty: true,
      message: 'Image data is unavailable.',
      data: null,
    };
  }

  const data = imagesCriteria?.data || [];
  const wooRankStatus = imagesCriteria?.status || 'neutral';
  const importance = imagesCriteria?.importance || 0;
  const solvability = imagesCriteria?.solvability || 0;

  // Calculate image statistics
  const stats = calculateImageStats(data);

  // Check if data is empty (no images)
  const isEmpty = stats.totalImages === 0;

  // Use API status directly - special scenarios only affect messages
  const status = getStatus(wooRankStatus);
  const iconType = getIconType(wooRankStatus);

  return {
    status,
    iconType,
    importance: getImportance(importance),
    solvability: getSolvability(solvability),
    isEmpty,
    message: generateMessage(stats),
    data: {
      totalImages: stats.totalImages,
      withAlt: stats.withAlt,
      missingAlt: stats.missingAlt,
      missingAltImages: stats.missingAltImages,
    },
  };
};

export default transformAltAttribute;

